<template>
  <div class="table-wrapper" :class="{ isScrollable }">
    <transition name="fade" mode="out-in">
      <div v-if="!removeBulkActions && state.bulk.items.length" class="bulk-actions-wrapper">
        <div class="bulk-infos">
          <div class="bulk-number" v-text="state.bulk.items.length" />
          <div class="bulk-label">{{ $t("app.table.selected") }}</div>
        </div>
        <div class="bulk-action btn sm icon icon-left" v-for="(action, index) in bulk.actions.filter((elt) => elt.display(state.bulk.items))" :key="index" :class="action.class" @click="action.action(state.bulk.items)">
          <i :class="`bi ${action.icon}`" />
          <span v-text="action.label" />
        </div>
        <div v-if="bulk.actions.filter((elt) => elt.display(state.bulk.items)).length === 0" class="bulk-action">
          {{ $t("app.table.noAction") }}
        </div>
      </div>
    </transition>
    <transition name="fade" mode="out-in">
      <div v-if="currentPageItems.length > 0" class="table-container" :class="{ isScrollable }" key="table" ref="tableContainer">
        <table>
          <thead>
            <tr>
              <th v-if="bulk">
                <div class="container">
                  <input type="checkbox" id="bulkAll" :checked="state.bulk.bulked.includes(state.currentPage)" @click.stop="toggleBulkAll" />
                </div>
              </th>
              <th v-for="field in fields" :class="{ sortable: field.sort, center: field.center }" :key="field.id" @click="field.sort ? sortColumn(field) : null">
                <div class="container">
                  <span>{{ field.label }}</span>
                  <div v-if="field.sort" class="sort" :class="state.sort.id === field.id ? sortDir : ''">
                    <i class="bi bi-caret-up-fill" />
                    <i class="bi bi-caret-down-fill" />
                  </div>
                </div>
              </th>
              <th v-if="actions" style="width: 0">Actions</th>
            </tr>
          </thead>
          <transition name="fade" mode="out-in">
            <tbody
              :key="
                JSON.stringify({
                  sort: state.sort,
                  currentPage: state.currentPage,
                })
              "
              :class="{ 'row-click': rowClick || rowClickToSelect }"
            >
              <tr v-for="(row, index) in currentPageItems" :style="changeColorOnCondition(row)" :key="Object.values(row)[0]" @click="handleRowClick(row)">
                <td v-if="bulk">
                  <div class="container">
                    <input type="checkbox" :id="`bulkRow-${index}`" :checked="state.bulk.items.includes(row)" @click.stop="toggleBulk(row)" />
                  </div>
                </td>
                <td v-for="field in fields" :key="field.id">
                  <slot :name="field.id" :data="row" :index="index">
                    <div v-if="field.template" class="container" v-safe-html="renderField(field, row)" />
                    <div v-else class="container" v-text="renderField(field, row)" />
                  </slot>
                </td>
                <td v-if="actions">
                  <div class="container">
                    <slot name="actions" :row="row" :index="index"></slot>
                  </div>
                </td>
              </tr>
            </tbody>
          </transition>
        </table>
      </div>
      <Empty v-else-if="search?.value" :message="$t('other.search.noResult')" :dark="isDark" icon="search-null" />
      <slot v-else name="empty" key="slot-empty"></slot>
    </transition>
  </div>
  <Pagination class="table-pagination" v-if="nbPages > 1" :nbPages="nbPages" :currentItem="state.currentPage" @changePage="updateCurrentPage" />
</template>

<script setup>
import { ref, reactive, computed, inject, onMounted, watch } from "vue";
import { get } from "lodash";
import { useDark } from "@vueuse/core";
import Empty from "@/components/table/Empty.vue";

const isDark = useDark();

const emitter = inject("emitter");

const emits = defineEmits(["clickOnRow", "selectedRow"]);

const tableContainer = ref(null);

const state = reactive({
  currentPage: 1,
  height: 0,
  sort: {
    id: null,
    func: null,
    dir: "ASC",
  },
  bulk: {
    bulked: [],
    items: [],
  },
});

const props = defineProps({
  fields: {
    type: Array,
    default: () => [],
  },
  data: {
    type: Array,
    default: () => {},
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  search: {
    type: Object,
  },
  bulk: {
    type: Object,
  },
  actions: {
    type: Boolean,
    default: true,
  },
  removeBulkActions: {
    type: Boolean,
    default: false,
  },
  rowClick: {
    type: Boolean,
    default: true,
  },
  rowClickToSelect: {
    type: Boolean,
    default: false,
  },
  rowStyleOnCondition: {
    type: Function,
    default: undefined,
  }
});

function changeColorOnCondition(row) {
  if (props.rowStyleOnCondition) return props.rowStyleOnCondition(row);
  else return undefined;
}

function handleRowClick(row) {
  if (props.rowClickToSelect) {
    return toggleBulk(row);
  } else if (props.rowClick) {
    return emits('clickOnRow', row);
  }
  return null;
}

const currentPageItems = computed(() => {
  let data = props.data;

  if (!data) return [];

  data = searchList(data);

  data = sortList(data);

  // Only adjust page if there are results and we're on an invalid page
  if (data.length > 0 && state.currentPage > 0 && data.length <= (state.currentPage - 1) * props.itemsPerPage) {
    updateCurrentPage(Math.max(1, state.currentPage - 1));
  }

  return data.slice((state.currentPage - 1) * props.itemsPerPage, props.itemsPerPage * state.currentPage);
});

// Pagination
const nbPages = computed(() => {
  const quotient = props.data.length / props.itemsPerPage;
  const rest = props.data.length % props.itemsPerPage;

  return rest ? Math.ceil(quotient) : quotient;
});

function updateCurrentPage(page) {
  state.currentPage = page;
}

// Search
function searchList(data) {
  if (!props.search?.value) return data;

  state.currentPage = 1;

  return data.filter((elt) => {
    let toReturn = false;

    props.search.searchable.forEach((field) => {
      let toCompare = [];

      if (field.combined) {
        field.keys.map((key) => {
          toCompare.push(get(elt, key));
        });

        toCompare = toCompare.join(" ");
      } else {
        toCompare = get(elt, field);
      }

      if (!toCompare) return;

      if (toCompare.toLowerCase().includes(props.search.value.toLowerCase())) toReturn = true;
    });

    return toReturn;
  });
}

// Sort
emitter.on("refreshTableData", () => {
  state.last_refresh = new Date();
});

const sortDir = computed(() => {
  return state.sort.dir === "ASC" ? "sort-asc" : "sort-desc";
});

function sortColumn(col) {
  let dir = null;

  if (state.sort.id === col.id) {
    dir = state.sort.dir === "ASC" ? "DESC" : "ASC";
  } else {
    dir = col.defaultSortDir ?? "ASC";
  }

  state.sort = {
    id: col.id,
    fallback: col.fallback,
    func: col.sort,
    dir,
    multipleSort: col.multipleSort,
  };
}

function sortList(data) {
  if (!state.sort.id) return data;

  data.sort((a, b) => {
    let objA, objB;

    if (state.sort.multipleSort) {
      objA = a;
      objB = b;
    } else {
      objA = typeof get(a, state.sort.id) !== "undefined" ? get(a, state.sort.id) : get(a, state.sort.fallback);
      objB = typeof get(b, state.sort.id) !== "undefined" ? get(b, state.sort.id) : get(b, state.sort.fallback);
    }

    return state.sort.func(objA, objB, state.sort.dir);
  });

  return data;
}

// Bulk
emitter.on("confirmBulk", () => {
  state.bulk = {
    bulked: [],
    items: [],
  };
});

function toggleBulk(row) {
  const index = state.bulk.items.indexOf(row);

  if (index > -1) {
    state.bulk.items.splice(index, 1);
  } else {
    state.bulk.items.push(row);
  }

  const nbItems = currentPageItems.value.length;

  if (state.bulk.items.length === nbItems) {
    state.bulk.bulked.push(state.currentPage);
  } else if (state.bulk.bulked.includes(state.currentPage)) {
    const index = state.bulk.bulked.findIndex((elt) => elt === state.currentPage);

    state.bulk.bulked.splice(index, 1);
  }
  emits("selectedRow", state.bulk.items);
}

function toggleBulkAll() {
  if (state.bulk.bulked.includes(state.currentPage)) {
    const index = state.bulk.bulked.findIndex((elt) => elt === state.currentPage);

    state.bulk.bulked.splice(index, 1);
    state.bulk.items = state.bulk.items.filter((elt) => !currentPageItems.value.includes(elt));
  } else {
    state.bulk.bulked.push(state.currentPage);
    state.bulk.items = [...new Set([...state.bulk.items, ...currentPageItems.value])];
  }
  emits("selectedRow", state.bulk.items);
}

// Global
function renderField(field, object) {
  const value = get(object, field.id);

  if (field.template) {
    return field.template(value);
  }

  return value;
}

const isScrollable = computed(() => {
  if (!state.height) return false;
  if (props.itemsPerPage >= 30) return true;

  return tableContainer.value.clientHeight < tableContainer.value.scrollHeight;
});

watch(props, () => {
  setTimeout(() => {
    state.height = tableContainer.value?.scrollHeight || 0;
  }, 100);

  state.bulk.items.forEach((elt) => {
    if (!props.data.includes(elt)) {
      const eltIndex = state.bulk.items.findIndex((obj) => obj === elt);
      state.bulk.items.splice(eltIndex, 1);
    }
  });
});

onMounted(() => {
  state.height = tableContainer.value?.scrollHeight || 0;

  window.addEventListener("resize", () => {
    state.height = tableContainer.value?.scrollHeight || 0;
  });
});
</script>
